import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LandingPage from "@/components/LandingPage";
import LandingPageV2 from "@/components/LandingPageV2";
import Dashboard from "@/components/Dashboard";
import Account from "@/components/Account";
import Pricing from "@/components/Pricing";
import Register from "@/components/Register";
import ThankYou from "@/components/ThankYou";
import AdminDashboard from "@/components/AdminDashboard";
import StripeSuccess from "@/components/StripeSuccess";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
});

// Add token to requests
axiosInstance.interceptors.request.use((config) => {
  // Check for admin token first, then regular token
  const adminToken = localStorage.getItem('admin_token');
  const token = localStorage.getItem('token');
  
  if (adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  } else if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle token expiration
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && error.response?.data?.detail?.includes('token')) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [newUsername, setNewUsername] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
    
    // Check if user should see thank you page (persisted in localStorage)
    const shouldShowThankYou = localStorage.getItem('showThankYou') === 'true';
    if (shouldShowThankYou && token) {
      setShowThankYou(true);
      setNewUsername(localStorage.getItem('username') || '');
    }

    // Check for payment success in URL
    const urlParams = new URLSearchParams(window.location.search);
    const targetingPayment = urlParams.get('targeting_payment');
    const supportPayment = urlParams.get('support_payment');
    
    if (targetingPayment === 'success') {
      // Show success notification after a brief delay
      setTimeout(() => {
        import('sonner').then(({ toast }) => {
          toast.success('Targeting Helper erfolgreich freigeschaltet! 🎉');
        });
      }, 500);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (targetingPayment === 'cancelled') {
      setTimeout(() => {
        import('sonner').then(({ toast }) => {
          toast.error('Zahlung abgebrochen.');
        });
      }, 500);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (supportPayment === 'success') {
      setTimeout(() => {
        import('sonner').then(({ toast }) => {
          toast.success('Support-Paket erfolgreich aktiviert! 🎉');
        });
      }, 500);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (supportPayment === 'cancelled') {
      setTimeout(() => {
        import('sonner').then(({ toast }) => {
          toast.error('Zahlung abgebrochen.');
        });
      }, 500);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleLogin = (token, username, isNewUser = false) => {
    localStorage.setItem('token', token);
    localStorage.setItem('username', username);
    setIsAuthenticated(true);
    
    if (isNewUser) {
      setNewUsername(username);
      setShowThankYou(true);
      // Persist showThankYou state for page refreshes
      localStorage.setItem('showThankYou', 'true');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('showThankYou');
    setIsAuthenticated(false);
    setShowThankYou(false);
  };
  
  // Function to clear thank you state (called after redirect to dashboard)
  const clearThankYouState = () => {
    localStorage.removeItem('showThankYou');
    setShowThankYou(false);
  };

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                showThankYou ? (
                  <ThankYou />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              ) : (
                <LandingPageV2 onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/pricing"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Pricing />
              )
            }
          />
          <Route
            path="/register"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Register onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/thank-you"
            element={
              isAuthenticated ? (
                <ThankYou />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                showThankYou ? (
                  <Navigate to="/thank-you" replace />
                ) : (
                  <Dashboard onLogout={handleLogout} />
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/account"
            element={
              isAuthenticated ? (
                <Account />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LandingPageV2 onLogin={handleLogin} openLoginModal={true} />
              )
            }
          />
          <Route
            path="/admin"
            element={<AdminDashboard />}
          />
          <Route
            path="/success"
            element={<StripeSuccess />}
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
